import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { SEO, faqSchema, breadcrumbSchema } from '../components/SEO';

const faqData = [
  {
    question: "What water depth do I need for Tenjam furniture?",
    answer: "Each product is designed for a specific water depth range. The Drift Stools work in up to 7 inches, the Ripple tables in up to 10 inches, the Freelo and Moon Chair in up to 11-12 inches, and the Shayz handles up to 17 inches with custom risers. Check the product listing for your specific depth requirement."
  },
  {
    question: "What materials are Tenjam products made from?",
    answer: "Tenjam furniture is manufactured from high-density, UV-stabilized, recyclable materials designed to resist chlorine, saltwater, and intense sun exposure. The materials will not fade, crack, or degrade under normal pool conditions. All products are 100% recyclable at end of life."
  },
  {
    question: "How does the Speed Fill Ballast System work?",
    answer: "The Speed Fill Ballast System uses built-in valves that allow pool water to flow into hollow cavities within the furniture. This water weight anchors the furniture securely to your sun shelf. Filling takes minutes and draining is just as fast when you need to move or store the furniture."
  },
  {
    question: "Can I use the furniture on my patio, not just in the pool?",
    answer: "Yes. Every Tenjam product works on dry surfaces just as well as in the pool. The Mibster, Woosah, and Freelo chairs transition from pool to patio without any draining or modification. Many customers keep their furniture on the deck when the pool is closed for the season."
  },
  {
    question: "What is the weight capacity?",
    answer: "Most Tenjam chairs support up to 400 pounds. Tables and stools have weight ratings appropriate for their intended use. Check the specific product listing for exact capacity numbers."
  },
  {
    question: "How do I clean and maintain the furniture?",
    answer: "For everyday cleaning, warm water and a Magic Eraser should handle it beautifully. If you're dealing with stubborn stains, mineral buildup or hard water residue, a little dish soap will give you that extra cleaning power."
  },
  {
    question: "Will the furniture damage my pool finish?",
    answer: "Tenjam products are designed with smooth, non-abrasive bottoms that rest gently on pool surfaces. The furniture will not scratch, scuff, or stain standard pool finishes including plaster, pebble, tile, and fiberglass."
  },
  {
    question: "Do I need to remove furniture from the pool in winter?",
    answer: "For regions with freezing temperatures, we recommend removing furniture from the pool before winterizing. The materials can handle cold weather on your patio, but water expansion during freezing inside the ballast cavities could cause damage. Drain the ballast and store upright or flat in a garage or covered area."
  },
  {
    question: "Can I mix different Tenjam products on the same sun shelf?",
    answer: "Absolutely. Our products are designed to complement each other. A popular combination is two Shayz or Laylo loungers with a Ripple side table between them. The Dash Bench and Drift Stools add versatile seating for guests without crowding your shelf space."
  },
  {
    question: "What sun shelf size do I need for your furniture?",
    answer: "Sun shelf size depends on the products you choose. A pair of loungers with a side table typically needs a shelf at least 5 feet wide and 8 feet long. Check individual product dimensions on each listing and measure your shelf before purchasing. Our support team can help with layout planning."
  },
  {
    question: "Are the colors customizable?",
    answer: "Several products offer made-to-order color options. The Shayz comes in Desert Sandstone, Gray Granite, Light Blue, White, and White Granite. Custom color orders typically add a few weeks to delivery time. Contact our team for current color availability."
  },
  {
    question: "Where is Tenjam furniture manufactured?",
    answer: "All Tenjam products are made in the United States. Domestic manufacturing allows us to maintain strict quality control and reduce delivery times. Every piece goes through inspection before shipping to ensure it meets our durability and finish standards."
  }
];

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-gray-200 last:border-b-0">
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between py-5 px-6 text-left hover:bg-gray-50 transition-colors duration-200"
      data-testid={`faq-toggle-${question.slice(0, 20).toLowerCase().replace(/\s+/g, '-')}`}
    >
      <h3 className="text-lg font-medium text-gray-900 pr-4">{question}</h3>
      {isOpen ? <ChevronUp size={20} className="text-cyan-600 flex-shrink-0" /> : <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />}
    </button>
    {isOpen && (
      <div className="px-6 pb-5">
        <p className="text-gray-600 leading-relaxed">{answer}</p>
      </div>
    )}
  </div>
);

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-20">
      <SEO
        title="FAQ | In-Pool Furniture Questions Answered"
        description="Frequently asked questions about Tenjam SPLASH Series in-pool furniture. Learn about water depths, materials, cleaning, weight capacity, and installation."
        canonical="https://rezvanifurniturestudio.com/faq"
        jsonLd={[
          faqSchema(faqData),
          breadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'FAQ' }
          ])
        ]}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">Frequently Asked Questions</h1>
          <p className="text-lg text-gray-600">Everything you need to know about Tenjam in-pool furniture</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {faqData.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
            />
          ))}
        </div>

        <div className="mt-12 text-center bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Still have questions?</h2>
          <p className="text-gray-600 mb-6">Our team is here to help you find the perfect in-pool furniture for your setup.</p>
          <a
            href="/contact"
            className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
          >
            Contact Us
          </a>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
